/**
 * Shared UI constants for the data-connections feature.
 *
 * Centralises color tokens and status values so they remain consistent
 * across components and are easy to update in one place.
 */

// ─── Connection Status ────────────────────────────────────────────────────────

export const ConnectionStatus = {
  Active: "active",
  Inactive: "inactive",
} as const

export type ConnectionStatusValue = (typeof ConnectionStatus)[keyof typeof ConnectionStatus]

/**
 * 3-state data connection indicator enum:
 * - Active: asset is transmitting live readings
 * - Inactive: asset has a connection/configuration but is not actively sending data
 * - NotConfigured: asset has no data connection configured
 */
export enum DataConnectionState {
  Active = "active",
  Inactive = "inactive",
  NotConfigured = "not_configured",
}

export type DataConnectionStateValue = `${DataConnectionState}`

// ─── Colors ───────────────────────────────────────────────────────────────────

/** Brand accent — used for active state indicators, switches, and lock icons. */
export const COLOR_ACCENT = "#39aed2"

/** Indicator hover accent */
export const COLOR_CONNECTION_HOVER = "#0bc5ea"

/** Indicator inactive dot color (solid gray per design specification) */
export const COLOR_CONNECTION_INACTIVE = "#a9a9a9"

/** Muted gray for inactive switch backgrounds. */
export const COLOR_SWITCH_INACTIVE = "#C4C4C4"

/** Amber/orange used to flag missing or stale source mappings. */
export const COLOR_WARNING = "#f0986f"

/** Warning row background (semi-transparent orange). */
export const COLOR_WARNING_BG = "rgba(240,152,111,0.12)"

/** Dark teal for source instance badge text. */
export const COLOR_SOURCE_BADGE_TEXT = "#19596D"

/** Light cyan used for source instance badge bg and dropdown highlights. */
export const COLOR_HIGHLIGHT_BG = "#eaf7fa"

// ─── Connector Type Configuration ─────────────────────────────────────────────

export type ConnectorTypeKey = "VRM" | "FCSL" | "ENAPTER" | "RMS" | "TYCROP" | "MQTT" | "COMAP"

/** One editable credential field rendered by the create-connector form.
 *  When a connector config declares `credentialFields`, the form renders one
 *  input per entry and JSON.stringify's the collected values into the single
 *  `vaultSecret` mutation argument. When absent, the connector keeps the
 *  existing single opaque-string credential input (backwards compatible). */
export interface CredentialField {
  /** JSON key written into the vaultSecret blob (must match adapter expectations). */
  key: string
  /** Human-readable label for the input. */
  label: string
  /** Render as a masked/password input and never echo the value. Default false. */
  secret?: boolean
  /** Whether the field must be non-empty to submit. Default true. */
  required?: boolean
  /** Optional placeholder text. */
  placeholder?: string
}

export interface ConnectorTypeConfig {
  displayName: string
  externalIdLabel: string
  externalIdPlaceholder: string
  /** Label for the "Create a new ___" checkbox */
  createNewLabel: string
  /** Placeholder text when no existing entries are available */
  noneAvailableLabel: string
  /** Placeholder text for the select dropdown */
  selectExistingLabel: string
  /** Tooltip explaining what a "source" means for this connector type */
  sourceTooltip: string
  /** Whether this connector type requires an external ID (installation/device identifier) to create a connection.
   *  When false, the connector is internally monitored and needs no additional configuration. */
  requiresExternalId: boolean
  /** Whether this connector type requires activation configuration dialog.
   *  When true, the activation dialog is shown regardless of asset kind. */
  requiresActivationConfig: boolean
  /** Default device model to auto-select when creating connections (e.g. "zwg" for Tycrop).
   *  When set, this value is passed as deviceModel without showing a selector. */
  defaultDeviceModel?: string
  /** Optional multi-field credential entry. Absent ⇒ single opaque-string field. */
  credentialFields?: CredentialField[]
}

export const CONNECTOR_CONFIG: Record<ConnectorTypeKey, ConnectorTypeConfig> = {
  VRM: {
    displayName: "Victron VRM",
    externalIdLabel: "Installation ID",
    externalIdPlaceholder: "Enter VRM Installation ID",
    createNewLabel: "Create a new installation",
    noneAvailableLabel: "No installations available",
    selectExistingLabel: "Select existing installation...",
    sourceTooltip: "A source is a reading we pull from the connected dataset. Which sources you map depends on the topology of the system: they typically follow its data buses, and the number in brackets (e.g. [0], [1]) identifies which instance on a bus you're mapping.",
    requiresExternalId: true,
    requiresActivationConfig: false,
  },
  FCSL: {
    displayName: "Fuel Cell Systems",
    externalIdLabel: "Device Identifier",
    externalIdPlaceholder: "Enter FCSL Device Identifier",
    createNewLabel: "Create a new device identifier",
    noneAvailableLabel: "No device identifiers available",
    selectExistingLabel: "Select existing device identifier...",
    sourceTooltip: "A source is a reading we pull from the connected dataset. One device can expose several different sets of data that can be monitored as an asset such as a pressure reading or refueller status.",
    requiresExternalId: true,
    requiresActivationConfig: false,
  },
  ENAPTER: {
    displayName: "Enapter",
    externalIdLabel: "Device ID",
    externalIdPlaceholder: "Enter Enapter Device UUID",
    createNewLabel: "Create a new device",
    noneAvailableLabel: "No devices available",
    selectExistingLabel: "Select existing device...",
    sourceTooltip: "A source represents an Enapter electrolyser. Each device maps to one asset.",
    requiresExternalId: true,
    requiresActivationConfig: false,
  },
  RMS: {
    displayName: "RMS",
    externalIdLabel: "",
    externalIdPlaceholder: "",
    createNewLabel: "",
    noneAvailableLabel: "No inputs available",
    selectExistingLabel: "Please select...",
    sourceTooltip: "RMS monitors connect directly to a system with no additional configuration required.",
    requiresExternalId: false,
    requiresActivationConfig: false,
    defaultDeviceModel: "standard"
  },
  TYCROP: {
    displayName: "Tycrop",
    externalIdLabel: "Device ID",
    externalIdPlaceholder: "Enter Tycrop ZWG Device ID",
    createNewLabel: "Create a new device",
    noneAvailableLabel: "No devices available",
    selectExistingLabel: "Select existing device...",
    sourceTooltip: "A source represents a connection point on a Tycrop ZWG hybrid system (BMS, DGS, Tank, or Load Controller).",
    requiresExternalId: true,
    requiresActivationConfig: false,
    defaultDeviceModel: "zwg"
  },
  MQTT: {
    displayName: "MQTT",
    externalIdLabel: "",
    externalIdPlaceholder: "",
    createNewLabel: "",
    noneAvailableLabel: "No inputs available",
    selectExistingLabel: "Please select...",
    sourceTooltip: "MQTT connectors publish data directly over MQTT. Select a source to map to an asset.",
    requiresExternalId: false,
    requiresActivationConfig: false,
    defaultDeviceModel: "standard"
  },
  COMAP: {
    displayName: "ComAp WebSupervisor",
    externalIdLabel: "ComAp unit GUID",
    externalIdPlaceholder: "Enter ComAp unit GUID",
    createNewLabel: "Create a new unit",
    noneAvailableLabel: "No units available",
    selectExistingLabel: "Select existing unit...",
    sourceTooltip: "A source is a ComAp genset. Each unit exposes a genset and a fuel tank you can map as separate assets.",
    requiresExternalId: true,
    requiresActivationConfig: false,
    defaultDeviceModel: "standard",
    credentialFields: [
      { key: "clientId", label: "Client ID", required: true },
      { key: "secret", label: "Client Secret", required: true, secret: true },
      { key: "loginId", label: "Login ID", required: true },
    ],
  },
} as const

/** Get connector config for a given type, with fallback for unknown types. */
export function getConnectorConfig(connectorType: string): ConnectorTypeConfig {
  return (
    CONNECTOR_CONFIG[connectorType as ConnectorTypeKey] ?? {
      displayName: connectorType,
      externalIdLabel: "External ID",
      externalIdPlaceholder: "Enter external ID",
      createNewLabel: "Create a new entry",
      noneAvailableLabel: "No entries available",
      selectExistingLabel: "Select existing entry...",
      sourceTooltip: "A source represents a data point from the connected device.",
      requiresExternalId: true,
      requiresActivationConfig: false,
    }
  )
}

// ─── FloatingDropdown palette ─────────────────────────────────────────────────

export const DROPDOWN_COLORS = {
  background: "#fff",
  backgroundDisabled: "#f9fafb",
  border: "#d4d4d8",
  borderMenu: "#e4e4e7",
  textPrimary: "#1a1a1a",
  textDisabled: "#a1a1aa",
  textMuted: "#71717a",
} as const

// ─── AddMappingRow ────────────────────────────────────────────────────────────

export const ADD_ROW_BG = "#f0fafe"
export const ADD_ROW_BORDER = "#d4eef7"
